import { useCallback } from 'react';
import DiscardConfirmation, {
  useDiscardConfirmation,
} from 'components/DiscardConfirmation';
import useSelectResource from 'hooks/useSelectResource';
import {
  NEW_ORG_NODE_ID,
  type TOrganizationNodeDrawer,
  createOrganization,
  deleteNodeLocal,
  deleteOrganizationNode,
  onDiscardForm,
  organizationMapSelector,
  saveOrganizationNode,
  selectFirstDrawer,
  selectSecondDrawer,
  updateDrawer,
} from 'slices/organizationHierarchy.slice';
import { useAppDispatch, useAppSelector } from 'slices/store';
import { errorNotification } from 'utils/notifications';
import {
  Button,
  ConfirmationPopup,
  Icon,
  TEDrawer,
} from '@timeedit/ui-components';
import { Space } from '@timeedit/ui-components/antd';
import { OrgDrawerContent } from './OrganizationDrawerContent';

const Drawer = () => {
  const dispatch = useAppDispatch();

  const firstDrawer = useAppSelector(selectFirstDrawer);
  const secondDrawer = useAppSelector(selectSecondDrawer);
  const nodesMap = useAppSelector(organizationMapSelector);

  const setFirstDrawer = useCallback(
    (data: TOrganizationNodeDrawer) => {
      dispatch(updateDrawer({ isFirstDrawer: true, data }));
    },
    [dispatch]
  );
  const setSecondDrawer = useCallback(
    (data: TOrganizationNodeDrawer) => {
      dispatch(updateDrawer({ isFirstDrawer: false, data }));
    },
    [dispatch]
  );

  const setFirstResource = useCallback(
    (orgNode: TOrganizationNodeDrawer) => setFirstDrawer(orgNode),
    [setFirstDrawer]
  );
  const setSecondResource = useCallback(
    (orgNode: TOrganizationNodeDrawer) => setSecondDrawer(orgNode),
    [setSecondDrawer]
  );

  useSelectResource(
    'objectId',
    nodesMap,
    firstDrawer,
    secondDrawer,
    setFirstResource,
    setSecondResource,
    NEW_ORG_NODE_ID
  );

  const [
    openDiscardDialog,
    onCloseDiscardDialog,
    confirmFn,
    confirmDiscardChanged,
  ] = useDiscardConfirmation();
  // ----- functions ----- //
  const _setDataFirstDrawer = ({ key, value }: { key: string; value: any }) => {
    setFirstDrawer({
      ...firstDrawer,
      [key]: value,
      isEdited: true,
    });
  };
  const _setDataSecondDrawer = ({
    key,
    value,
  }: {
    key: string;
    value: any;
  }) => {
    setSecondDrawer({
      ...secondDrawer,
      [key]: value,
      isEdited: true,
    });
  };
  const _handleSaveFirstDrawer = async () => {
    const { isEdited, idTable, ...rest } = firstDrawer;
    if (rest.name.includes('|')) {
      errorNotification('Organizations name cannot include |');
      return;
    }
    if (firstDrawer.id === NEW_ORG_NODE_ID) {
      dispatch(createOrganization(firstDrawer));
    } else {
      dispatch(
        saveOrganizationNode({ organizationNode: rest, isFirstDrawer: true })
      );
    }
  };
  const _handleSaveSecondDrawer = async () => {
    const { isEdited, idTable, ...rest } = secondDrawer;
    if (rest.name.includes('|')) {
      errorNotification('Organizations name cannot include |');
      return;
    }
    dispatch(
      saveOrganizationNode({ organizationNode: rest, isFirstDrawer: false })
    );
  };
  const _delFirstDrawer = async () => {
    if (firstDrawer.id === NEW_ORG_NODE_ID) {
      dispatch(deleteNodeLocal());
    } else {
      await dispatch(
        deleteOrganizationNode({
          isFirstDrawer: true,
          organizationNodeId: firstDrawer.id,
        })
      );
    }
  };
  const _delSecondDrawer = async () => {
    await dispatch(
      deleteOrganizationNode({
        isFirstDrawer: false,
        organizationNodeId: secondDrawer.id,
      })
    );
  };

  return (
    <>
      <TEDrawer
        expandWidth={'70vw'}
        defaultExpand={false}
        firstDrawer={{
          title: firstDrawer?.name,
          content: (
            <OrgDrawerContent
              onChange={_setDataFirstDrawer}
              data={firstDrawer}
            />
          ),
          extra: (
            <DrawerExtra
              drawer={firstDrawer}
              handleDelete={_delFirstDrawer}
              handleDiscard={() =>
                dispatch(
                  onDiscardForm({ isFirstDrawer: true, id: firstDrawer.id })
                )
              }
              handleSave={_handleSaveFirstDrawer}
            />
          ),
          drawerOpen: !!firstDrawer,
          setDrawerOpen: (val) => {
            if (!val && firstDrawer?.isEdited) {
              confirmDiscardChanged(() => {
                if (firstDrawer.id === NEW_ORG_NODE_ID) {
                  dispatch(deleteNodeLocal());
                }
                setFirstDrawer(null);
              });
              return false;
            }
            if (firstDrawer.id === NEW_ORG_NODE_ID) {
              dispatch(deleteNodeLocal());
            }
            setFirstDrawer(null);
          },
        }}
        secondDrawer={{
          title: secondDrawer?.name,
          content: (
            <OrgDrawerContent
              onChange={_setDataSecondDrawer}
              data={secondDrawer}
            />
          ),
          drawerOpen: !!secondDrawer,
          setDrawerOpen: (val) => {
            if (!val && secondDrawer?.isEdited) {
              confirmDiscardChanged(() => {
                setSecondDrawer(null);
              });
              return false;
            }
            setSecondDrawer(null);
          },
          extra: (
            <DrawerExtra
              drawer={secondDrawer}
              handleDelete={_delSecondDrawer}
              handleDiscard={() =>
                dispatch(
                  onDiscardForm({ isFirstDrawer: false, id: secondDrawer.id })
                )
              }
              handleSave={_handleSaveSecondDrawer}
            />
          ),
        }}
        onExpand={() => null}
      />
      <DiscardConfirmation
        open={openDiscardDialog}
        onClose={onCloseDiscardDialog}
        confirmFn={confirmFn}
      />
    </>
  );
};

export default Drawer;

type TExtra = {
  drawer: any;
  handleDelete: () => void;
  handleDiscard: () => void;
  handleSave: () => void;
};
const DrawerExtra = ({
  drawer,
  handleDelete,
  handleDiscard,
  handleSave,
}: TExtra) => {
  return (
    <Space>
      {' '}
      <ConfirmationPopup
        message={`Are you sure to delete ${drawer?.name}?`}
        onConfirm={handleDelete}
        okText="Delete"
        cancelText="Keep"
      >
        <Button
          style={{ marginRight: 8 }}
          size="small"
          icon={<Icon id="DeleteOutlined" alt="Delete" />}
        />{' '}
      </ConfirmationPopup>
      <Button size="small" onClick={handleDiscard} disabled={!drawer?.isEdited}>
        Discard
      </Button>
      <Button
        size="small"
        type={drawer?.isEdited ? 'primary' : 'default'}
        disabled={!drawer?.isEdited}
        onClick={handleSave}
      >
        Save
      </Button>{' '}
    </Space>
  );
};
